import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FaCircleXmark } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import {
  AddButton,
  Button,
  Input,
  SubTitle,
} from "../../Components/CustomComponents";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import {
  createInvoice,
  fetchAppointments,
  fetchInvoices,
} from "../../redux/slices";
import modalStyles from "../../Components/Modals/styles/editModal.module.scss";
import styles from "./styles/invoice_management.module.scss";

function getAppointmentSelectLabel(a) {
  const user = a.User?.name ?? a.user?.name ?? "";
  const date = a.date ?? "";
  const parts = [`#${a.id}`];
  if (date) parts.push(date);
  if (user) parts.push(user);
  return parts.join(" · ");
}

function formatMoney(cell) {
  const n = Number(cell ?? 0);
  return `$${Number.isFinite(n) ? n.toFixed(2) : "0.00"}`;
}

function formatServiceCell(row) {
  const arr = row.service;
  if (!Array.isArray(arr) || !arr.length) return "—";
  const titles = arr.map((s) => s?.title ?? s?.name).filter(Boolean);
  return titles.length ? titles.join(", ") : "—";
}

const InvoiceManagement = () => {
  const dispatch = useDispatch();
  const rowData = useSelector((state) => state.invoice.list);
  const invoicesLoading = useSelector((state) => state.invoice.loading);
  const creating = useSelector((state) => state.invoice.creating);

  const appointments = useSelector((state) => state.appointment.list);
  const appointmentsLoading = useSelector((state) => state.appointment.loading);

  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [createModal, setCreateModal] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
  const [amount, setAmount] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    dispatch(fetchInvoices())
      .unwrap()
      .catch((err) => {
        const msg =
          err?.body?.message ??
          err?.data?.message ??
          err?.message ??
          "Could not load invoices";
        Toast(msg, "error");
      });
  }, [dispatch]);

  useEffect(() => {
    if (invoicesLoading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No invoices found");
  }, [invoicesLoading, rowData.length]);

  useEffect(() => {
    if (!createModal) return;
    dispatch(fetchAppointments());
  }, [createModal, dispatch]);

  const appointmentOptions = useMemo(
    () => (Array.isArray(appointments) ? appointments : []),
    [appointments]
  );

  const handleCreateModalOpen = () => setCreateModal(true);

  const handleCreateModalClose = () => {
    setCreateModal(false);
    setSelectedAppointmentId("");
    setAmount("");
    setDepositAmount("");
    setStatus("");
  };

  const handleCreateInvoice = async () => {
    if (!selectedAppointmentId) {
      Toast("Please select an appointment", "error");
      return;
    }
    if (!amount) {
      Toast("Please enter amount and deposit amount", "error");
      return;
    }
    if (!status) {
      Toast("Please select a status", "error");
      return;
    }

    const payload = {
      appointment_id: Number(selectedAppointmentId),
      amount: Number(amount),
      deposit_amount: Number(depositAmount),
      status,
    };

    try {
      await dispatch(createInvoice(payload)).unwrap();
      Toast("Invoice created successfully", "success");
      handleCreateModalClose();
    } catch (err) {
      const msg =
        err?.body?.message ??
        err?.data?.message ??
        err?.message ??
        "Could not create invoice";
      Toast(msg, "error");
    }
  };

  const columns = [
    {
      dataField: "invoice_no",
      text: "Invoice #",
      sort: true,
      headerStyle: { width: "120px" },
      formatter: (cell) => cell ?? "—",
    },
    {
      dataField: "appointment_id",
      text: "Appointment ID",
      sort: true,
      headerStyle: { width: "130px" },
      formatter: (cell) => (cell != null ? String(cell) : "—"),
    },
    {
      dataField: "artist_name",
      text: "Artist",
      sort: true,
      headerStyle: { width: "160px" },
      formatter: (cell) => cell ?? "—",
    },
    {
      dataField: "location",
      text: "Location",
      sort: true,
      headerStyle: { width: "140px" },
      formatter: (cell) => cell ?? "—",
    },
    {
      dataField: "service",
      text: "Service",
      sort: false,
      headerStyle: { width: "180px" },
      formatter: (_cell, row) => formatServiceCell(row),
    },
    {
      dataField: "booking_date",
      text: "Booking date",
      sort: true,
      headerStyle: { width: "130px" },
      formatter: (cell) => cell ?? "—",
    },
    {
      dataField: "time",
      text: "Time",
      sort: true,
      headerStyle: { width: "100px" },
      formatter: (cell) => cell ?? "—",
    },
    {
      dataField: "amount",
      text: "Amount",
      sort: true,
      headerStyle: { width: "120px" },
      formatter: (cell) => formatMoney(cell),
    },
    {
      dataField: "deposit_amount",
      text: "Deposit",
      sort: true,
      headerStyle: { width: "120px" },
      formatter: (cell) => formatMoney(cell),
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: { width: "120px" },
      formatter: (cell) => (
        console.log(cell,'asdasdasd'),
        <span className={ cell === "paid" ? styles.paidStatusBadge : styles.unpaidStatusBadge }>{(cell || "").toUpperCase()}</span>
      ),
    },
  ];

  const indication = () => indicationMessage;

  return (
    <div className={styles.MainContainer}>
      {createModal ? (
        <ModalContainer handleClose={handleCreateModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Create Invoice</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleCreateModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>

            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Appointment</p>
                  <Form.Select
                    className={`${styles.selectField} mt-2 mb-3`}
                    value={selectedAppointmentId}
                    onChange={(e) => setSelectedAppointmentId(e.target.value)}
                    disabled={appointmentsLoading}
                  >
                    <option value="">
                      {appointmentsLoading
                        ? "Loading appointments..."
                        : appointmentOptions.length === 0
                        ? "No appointments available"
                        : "Select appointment"}
                    </option>
                    {appointmentOptions.map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {getAppointmentSelectLabel(a)}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Amount</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="number"
                    step="0.01"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Deposit Amount</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="number"
                    step="0.01"
                    placeholder="Enter deposit amount"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Status</p>
                  <Form.Select
                    className={`${styles.selectField} mt-2 mb-3`}
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">Select status</option>
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                  </Form.Select>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-4"
                    text={creating ? "Creating..." : "Create Invoice"}
                    onClick={handleCreateInvoice}
                    disabled={creating}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : (
        ""
      )}

      <div className={styles.headerContainer}>
        <div className="w-100 flex-between">
          <SubTitle text={"Invoice Management"} />
          <AddButton text="Create Invoice" onClick={handleCreateModalOpen} />
        </div>
      </div>

      <div className={styles.mainTable}>
        <Table data={rowData} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default InvoiceManagement;
